import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import * as bookingApi from "./bookingApi";
import { Header, Mono, Page, PreviewBar } from "./parts";
import { LinkNotFound } from "./ManagePage";
import { Logo, readKind, useWide } from "./desktop";
import { clock, longDay, offsetName, whenLabel, zoneCity } from "./format";

/*
 * /book/read/:id — one "Before we talk" page, INSIDE the booking app. Ruled
 * 2026-09-30: "I don't want the user to leave the booking app." Written on the
 * CRM's Page tab; opened from the success screen; always one tap back to the
 * booking it came from.
 *
 * `b` is the booking's own token, so "Back to your booking" returns to that
 * success screen. `p`/`as`/`tz` carry a CRM preview through the same round trip.
 */

/*
 * The body's formatting, deliberately small: "## " headings, "- " bullets,
 * blank lines between paragraphs, **bold**, and [text](https://…) links. Built
 * as React elements, never as HTML, so nothing typed in the CRM can inject
 * markup into a lead's page.
 */
function inline(text, keyBase) {
  const out = [];
  const pattern = /\*\*([^*]+)\*\*|\[([^\]]+)\]\((https:\/\/[^\s)]+)\)/g;
  let last = 0;
  let m;
  let n = 0;
  while ((m = pattern.exec(text))) {
    if (m.index > last) out.push(text.slice(last, m.index));
    if (m[1]) out.push(<strong key={`${keyBase}-${n++}`}>{m[1]}</strong>);
    else
      out.push(
        <a key={`${keyBase}-${n++}`} href={m[3]} target="_blank" rel="noreferrer">
          {m[2]}
        </a>
      );
    last = pattern.lastIndex;
  }
  if (last < text.length) out.push(text.slice(last));
  return out;
}

export function Body({ text }) {
  const blocks = String(text || "")
    .replace(/\r/g, "")
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean);
  return (
    <div className="bk-read-body">
      {blocks.map((block, i) => {
        const lines = block.split("\n");
        if (lines.every((l) => /^\s*[-•]\s+/.test(l))) {
          return (
            <ul key={i}>
              {lines.map((l, j) => (
                <li key={j}>{inline(l.replace(/^\s*[-•]\s+/, ""), `${i}-${j}`)}</li>
              ))}
            </ul>
          );
        }
        if (lines.length === 1 && /^#{1,3}\s+/.test(lines[0])) {
          return <h2 key={i}>{inline(lines[0].replace(/^#{1,3}\s+/, ""), `${i}`)}</h2>;
        }
        return (
          <React.Fragment key={i}>
            {lines.map((l, j) =>
              /^#{1,3}\s+/.test(l) ? (
                <h2 key={j}>{inline(l.replace(/^#{1,3}\s+/, ""), `${i}-${j}`)}</h2>
              ) : null
            )}
            {lines.some((l) => !/^#{1,3}\s+/.test(l)) ? (
              <p>
                {lines
                  .filter((l) => !/^#{1,3}\s+/.test(l))
                  .map((l, j, arr) => (
                    <React.Fragment key={j}>
                      {inline(l, `${i}-${j}`)}
                      {j < arr.length - 1 ? <br /> : null}
                    </React.Fragment>
                  ))}
              </p>
            ) : null}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default function ReadPage() {
  const { id } = useParams();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const back = query.get("b");
  const preview = query.get("p");
  const as = query.get("as");
  const tz = query.get("tz");
  const wide = useWide();

  const [page, setPage] = useState(null);
  const [error, setError] = useState(null);
  const [call, setCall] = useState(null);

  useEffect(() => {
    setPage(null);
    bookingApi
      .getRead({ id, preview })
      .then(setPage)
      .catch(setError);
  }, [id, preview]);

  // The booking this page was opened from, for "Your call" and the top bar.
  useEffect(() => {
    const load = preview
      ? bookingApi.getPreviewSuccess({ preview, tz, as })
      : back
      ? bookingApi.getManage({ token: back }).then((d) => d.booking)
      : Promise.resolve(null);
    load.then(setCall).catch(() => setCall(null));
  }, [preview, tz, as, back]);

  const trail = preview
    ? { p: preview, ...(as ? { as } : {}), ...(tz ? { tz } : {}) }
    : back
    ? { b: back }
    : {};
  const backTo = preview
    ? `/book/booked/preview?${new URLSearchParams(trail)}`
    : back
    ? `/book/booked/${encodeURIComponent(back)}`
    : "/book";
  const hrefFor = (other) => `/book/read/${encodeURIComponent(other)}?${new URLSearchParams(trail)}`;

  if (error && error.status === 404) return <LinkNotFound />;

  const video = page && page.video ? (
    <div className="bk-video">
      <iframe src={page.video.src} title={page.title} allow="autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen />
    </div>
  ) : null;

  /* DESKTOP (Remotion_Booking_-_Desktop.pptx, 3A): the page on the left, the call and the other pages on the right. */
  if (wide) {
    const zone = call ? call.timezone : null;
    return (
      <Page title={page ? `${page.title} · Remotion` : undefined}>
        {preview ? <PreviewBar /> : null}
        <div className="bkd-bar">
          <div className="bkd-bar__left">
            <Logo />
            <Link className="bkd-backlink" to={backTo}>
              ← Your booking
            </Link>
          </div>
          {call ? (
            <span className="bk-mono bkd-bar__right">
              {longDay(call.start, zone)} · {clock(call.start, zone)} ({offsetName(zone)})
            </span>
          ) : null}
        </div>
        <div className="bkd-body bkd-cols bkd-cols--read">
          <article className="bkd-article">
            {error ? <p className="bk-lede">{error.message}</p> : null}
            {!page && !error ? <p className="bk-muted">One moment…</p> : null}
            {page ? (
              <>
                <Mono className="bk-muted">
                  Before we talk{page.total > 1 ? ` · ${page.position} of ${page.total}` : ""}
                </Mono>
                <h1 className="bkd-article__title">{page.title}</h1>
                {page.teaser ? <p className="bkd-article__teaser">{page.teaser}</p> : null}
                {video}
                <Body text={page.body} />
                <Link className="bk-primary bk-primary--link bk-primary--fit" to={backTo}>
                  Back to your booking
                </Link>
              </>
            ) : null}
          </article>
          <aside className="bkd-side">
            {call ? (
              <>
                <Mono>Your call</Mono>
                <div className="bkd-callcard">
                  <b>{whenLabel(call.start, call.end, zone)}</b>
                  <span>
                    {zoneCity(zone)} time · Google Meet
                  </span>
                </div>
              </>
            ) : null}
            {page && page.others.length ? (
              <>
                <Mono>More before we talk</Mono>
                <div className="bkd-more">
                  {page.others.map((o) => (
                    <Link key={o.id} to={hrefFor(o.id)}>
                      <b>{o.title}</b>
                      <span>{readKind(o)}</span>
                    </Link>
                  ))}
                </div>
              </>
            ) : null}
          </aside>
        </div>
      </Page>
    );
  }

  return (
    <Page title={page ? `${page.title} · Remotion` : undefined}>
      {preview ? <PreviewBar /> : null}
      <Header kicker="Before we talk" right={<Link className="bk-back" to={backTo}>← Your booking</Link>}>
        {page ? page.title : " "}
      </Header>
      <div className="bk-body">
        {error ? <p className="bk-lede">{error.message}</p> : null}
        {!page && !error ? <p className="bk-muted">One moment…</p> : null}
        {page ? (
          <>
            {page.teaser ? <p className="bk-lede">{page.teaser}</p> : null}
            {video}
            <Body text={page.body} />
            <Link className="bk-primary bk-primary--link" to={backTo}>
              Back to your booking
            </Link>
            <Mono className="bk-read-sign">{page.host}, Remotion</Mono>
          </>
        ) : null}
      </div>
    </Page>
  );
}
